import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as graphicService from "../services/graphicService";

/**
 * A drawn tactical graphic as the map layer needs it.
 *
 * Unlike markers, graphics are not squeezed into `MapEntity`: that shape is
 * built around a single `latLng` plus milsymbol modifiers, and a multipoint
 * graphic has neither. Keeping its own shape means the geometry stays exactly as
 * stored — `[lng, lat]` pairs plus ground-distance params — with no lossy
 * conversion between the API, Redux and the renderer.
 */
export interface TacticalGraphic {
    id: string;
    sidc: string;
    /** Centreline vertices as [lng, lat], tail → tip. */
    points: number[][];
    /** Symbol parameters as ground distances in metres. */
    params: graphicService.GraphicParams;
    faction_id: string | null;
    /** Parent unit, or null. Association only — see the model. */
    unit_id: string | null;
    /** Parent marker, or null. Mutually exclusive with `unit_id`. */
    marker_id: string | null;
    name: string | null;
}

interface GraphicsState {
    value: TacticalGraphic[];
    isLoading: boolean;
}

const initialState: GraphicsState = {
    value: [],
    isLoading: false,
};

export function toTacticalGraphic(
    graphic: graphicService.GraphicResponse,
): TacticalGraphic {
    return {
        id: graphic.id,
        sidc: graphic.sidc,
        points: graphic.points,
        params: graphic.params ?? {},
        faction_id: graphic.faction_id,
        unit_id: graphic.unit_id,
        marker_id: graphic.marker_id ?? null,
        name: graphic.name,
    };
}

/** Fetch faction-scoped graphics for the selected faction. */
export const fetchFactionGraphics = createAsyncThunk<
    TacticalGraphic[],
    { eventId: string; factionId: string }
>('graphics/fetchFactionGraphics', async ({ eventId, factionId }) => {
    const response = await graphicService.getGraphics(eventId, factionId);
    return response.graphics.map(toTacticalGraphic);
});

/** Fetch event-wide (shared) graphics — no faction_id filter. */
export const fetchGlobalGraphics = createAsyncThunk<
    TacticalGraphic[],
    { eventId: string }
>('graphics/fetchGlobalGraphics', async ({ eventId }) => {
    const response = await graphicService.getGraphics(eventId);
    return response.graphics.map(toTacticalGraphic);
});

export const graphicsSlice = createSlice({
    name: 'graphics',
    initialState,
    reducers: {
        /**
         * Insert or replace a graphic, keyed on id.
         *
         * Upsert rather than add: a locally drawn graphic is stored optimistically
         * and then echoed back over MQTT, and both arrivals must converge on one
         * entry. Geometry is replaced wholesale — a half-updated shape is not a
         * meaningful state.
         */
        upsertGraphic: (state, action: PayloadAction<TacticalGraphic>) => {
            const idx = state.value.findIndex((g) => g.id === action.payload.id);
            if (idx === -1) {
                state.value.push(action.payload);
            } else {
                state.value[idx] = action.payload;
            }
        },
        removeGraphic: (state, action: PayloadAction<string>) => {
            state.value = state.value.filter((g) => g.id !== action.payload);
        },
        clearGraphics: (state) => {
            state.value = [];
        },
    },
    extraReducers: (builder) => {
        const upsertAll = (
            state: GraphicsState,
            action: PayloadAction<TacticalGraphic[]>,
        ) => {
            state.isLoading = false;
            // Drop the rows this fetch supersedes, then append the fresh ones, so
            // a faction fetch never discards the shared graphics (or vice versa).
            const incoming = new Set(action.payload.map((g) => g.id));
            state.value = state.value.filter((g) => !incoming.has(g.id));
            state.value.push(...action.payload);
        };

        builder
            .addCase(fetchFactionGraphics.pending, (state) => { state.isLoading = true; })
            .addCase(fetchFactionGraphics.fulfilled, upsertAll)
            .addCase(fetchFactionGraphics.rejected, (state) => { state.isLoading = false; })
            .addCase(fetchGlobalGraphics.pending, (state) => { state.isLoading = true; })
            .addCase(fetchGlobalGraphics.fulfilled, upsertAll)
            .addCase(fetchGlobalGraphics.rejected, (state) => { state.isLoading = false; });
    },
});

export const { upsertGraphic, removeGraphic, clearGraphics } = graphicsSlice.actions;

export default graphicsSlice.reducer;
